///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { recordCrumb } from "@/earthbrowser/utils/CrashSentinel"
import { DatasetLayer, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { WildfireFeature } from "./WildfireFeature"
import { WildfiresTable, type WildfireProperties } from "./WildfiresTable"

/** Raw Arrow row shape for the NIFC wildfire feed (only fields we read) */
interface WildfireRow {
	id: unknown
	name: unknown
	acres: unknown
	incident_size: unknown
	percent_contained: unknown
	fire_cause: string | null | undefined
	fire_behavior: string | null | undefined
	state: string | null | undefined
	county: string | null | undefined
	short_description: string | null | undefined
	personnel: unknown
	discovered: string | null | undefined
	contained: string | null | undefined
	fire_out: string | null | undefined
	date_current: unknown
	centroid_lat: unknown
	centroid_lon: unknown
	bbox_min_lon: unknown
	bbox_min_lat: unknown
	bbox_max_lon: unknown
	bbox_max_lat: unknown
	geometry: unknown
	ingested_at: unknown
}

export class WildfiresDatasetLayer extends DatasetLayer<WildfireProperties> {
	private unsubscribeRedux?: () => void

	constructor(context: EarthBrowserContext) {
		super(context, "wildfires")
		this.subscribeToVisibility()
	}

	private subscribeToVisibility(): void {
		this.visible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("wildfire") ?? false
		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const isVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("wildfire") ?? false
			if (isVisible !== this.visible) {
				this.visible = isVisible
			}
		})
	}

	override destroy(): void {
		this.unsubscribeRedux?.()
		super.destroy()
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "wildfires",
			pollIntervalMs: 300_000, // 5 minutes; NIFC publishes every ~10 min
			needsSync: true,
			supportsDelta: true,
			deltaOnlyIngest: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await WildfiresTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)
		await WildfiresTable.replaceAll(this.parseWildfireRows(rows))
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		// Full current-perimeter snapshot each publish (delta-only protocol),
		// so replaceAll also handles fires removed from the NIFC feed
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		await WildfiresTable.replaceAll(this.parseWildfireRows(upsertRows))

		if (delta.deletes?.path) {
			const deletesUrl = this.resolveUrl(delta.deletes.url, delta.deletes.path)
			const deletesRows = await downloadArrowRows(deletesUrl)
			const deletes = (deletesRows as Array<{ id: string }>).map(r => String(r.id))
			if (deletes.length > 0) {
				await WildfiresTable.deleteByIds(deletes)
			}
		}
	}

	private parseWildfireRows(rows: unknown[]): WildfireProperties[] {
		return (rows as WildfireRow[]).map(r => ({
			id: String(r.id),
			name: String(r.name),
			acres: r.acres === null || r.acres === undefined ? null : Number(r.acres),
			incident_size: r.incident_size === null || r.incident_size === undefined ? null : Number(r.incident_size),
			percent_contained: r.percent_contained === null || r.percent_contained === undefined ? null : Number(r.percent_contained),
			fire_cause: r.fire_cause ? String(r.fire_cause) : null,
			fire_behavior: r.fire_behavior ? String(r.fire_behavior) : null,
			state: r.state ? String(r.state) : null,
			county: r.county ? String(r.county) : null,
			short_description: r.short_description ? String(r.short_description) : null,
			personnel: r.personnel === null || r.personnel === undefined ? null : Number(r.personnel),
			discovered: r.discovered ? String(r.discovered) : null,
			contained: r.contained ? String(r.contained) : null,
			fire_out: r.fire_out ? String(r.fire_out) : null,
			date_current: String(r.date_current),
			centroid_lat: Number(r.centroid_lat),
			centroid_lon: Number(r.centroid_lon),
			bbox_min_lon: Number(r.bbox_min_lon),
			bbox_min_lat: Number(r.bbox_min_lat),
			bbox_max_lon: Number(r.bbox_max_lon),
			bbox_max_lat: Number(r.bbox_max_lat),
			geometry: String(r.geometry),
			ingested_at: String(r.ingested_at),
		}))
	}

	// ==================== Scene Objects ====================

	public async queryFeatures(db: Database): Promise<WildfireProperties[]> {
		// Cap the scene at the 1000 most recently updated perimeters; each
		// feature is a marker plus triangulated fill and outline geometry
		const rows = await db.exec(`
			select *
			from wildfires
			order by date_current desc
			limit 1000
		`) as WildfireProperties[]

		// Memory diagnostics: the geometry JSON strings dominate this layer's
		// data footprint and predict the triangulation cost that follows
		let geometryChars = 0
		for (const row of rows) {
			geometryChars += row.geometry.length
		}
		console.log(`[WildfiresDatasetLayer] ${rows.length} fires queried, ${(geometryChars * 2 / 1048576).toFixed(1)}MB geometry JSON`)
		recordCrumb(`wildfires:queried n=${rows.length} json=${(geometryChars * 2 / 1048576).toFixed(1)}MB`)

		return rows
	}

	public override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<WildfireProperties> | undefined> {
		const existing = this.getSceneObject(id)
		if (existing) {
			return existing
		}

		const fire = await WildfiresTable.getById(id)
		if (!fire) return undefined
		this.addSceneObject(fire)
		return this.sceneObjects.get(id)
	}

	public createSceneObject(feature: WildfireProperties): DatasetSceneObject<WildfireProperties> | undefined {
		return new WildfireFeature(this.context, feature)
	}
}
